// Global ordering utilities for consistent report display

// Section ordering for main report layout
export const SECTION_ORDER = [
  'summary', // Overview metrics at top
  'revenue', // Revenue breakdown (payment methods, sales channels)
  'customer', // Customer analytics
  'promotions', // Promotions & rewards
  'delivery', // Delivery performance
  'detailed', // Detailed order tables
  'daily', // Daily breakdown at bottom
];

// Payment method ordering (most common first)
export const TENDER_TYPE_ORDER = [
  'card',
  'cash',
  'check',
  'gift_card',
  'store_credit',
  'other',
];

// Card type ordering (by market share)
export const CARD_TYPE_ORDER = [
  'visa',
  'mastercard',
  'amex',
  'discover',
  'unknown',
];

// Sales channel ordering (internal first, then by volume)
export const SALES_SOURCE_ORDER = [
  'pos', // In-store POS first
  'kiosk', // Self-serve kiosk (POS provider, kiosk brand)
  'ubereats', // Major delivery platforms
  'doordash',
  'grubhubv2',
  'food2', // Website orders
  'table_ordering', // Table ordering
  'other',
];

// Order type ordering (by typical volume)
export const ORDER_TYPE_ORDER = [
  'pickup',
  'delivery',
  'dine_in',
  'in_store',
  'takeout',
  'other',
];

// Discount type ordering (by impact/frequency)
export const DISCOUNT_TYPE_ORDER = [
  'employee',
  'manager',
  'promotion',
  'coupon',
  'loyalty',
  'other',
];

// Refund type ordering (by payment method)
export const REFUND_TYPE_ORDER = ['card', 'cash', 'store_credit', 'other'];

// Discount type mapping for better display
export const DISCOUNT_TYPE_NAMES = {
  cash: 'Cash Discount',
  employee: 'Store Discounts',
  coupon: 'Coupon Discounts',
  manager: 'Manager Discount',
  promotion: 'Promotion Discount',
  loyalty: 'Loyalty Discount',
};

// Coupon ordering (by usage frequency - most used first)
export const sortCoupons = (coupons) => {
  return [...coupons].sort((a, b) => {
    // Sort by count (usage) descending, then by amount descending
    if (b.count !== a.count) {
      return b.count - a.count;
    }
    return b.totalCouponAmount - a.totalCouponAmount;
  });
};

// Generic sorting function with fallback for unknown items
const createSorter = (order) => {
  return (items) => {
    return [...items].sort((a, b) => {
      // Determine the key to use for sorting
      const aKey = (a._id || a.id || a.cardType || '').toLowerCase();
      const bKey = (b._id || b.id || b.cardType || '').toLowerCase();

      const aIndex = order.indexOf(aKey);
      const bIndex = order.indexOf(bKey);

      // Items not in order go to the end
      const aPos = aIndex === -1 ? order.length : aIndex;
      const bPos = bIndex === -1 ? order.length : bIndex;

      return aPos - bPos;
    });
  };
};

// Exported sorting functions
export const sortTenderTypes = createSorter(TENDER_TYPE_ORDER);
export const sortCardTypes = createSorter(CARD_TYPE_ORDER);
export const sortSalesBySource = createSorter(SALES_SOURCE_ORDER);
export const sortOrderTypes = createSorter(ORDER_TYPE_ORDER);
export const sortDiscounts = createSorter(DISCOUNT_TYPE_ORDER);
export const sortRefunds = createSorter(REFUND_TYPE_ORDER);

// Sort by amount (descending) for financial data
export const sortByAmountDesc = (items) => {
  return [...items].sort((a, b) => {
    const aAmount = a.totalAmount || a.amount || a.total || 0;
    const bAmount = b.totalAmount || b.amount || b.total || 0;
    return bAmount - aAmount;
  });
};

// Sort by count (descending) for usage data
export const sortByCountDesc = (items) => {
  return [...items].sort((a, b) => {
    const aCount = a.count || a.orderCount || 0;
    const bCount = b.count || b.orderCount || 0;
    return bCount - aCount;
  });
};

// Sort daily breakdown by date (ascending)
export const sortDailyBreakdown = (items) => {
  return [...items].sort((a, b) => {
    const aDate = new Date(a.date || a._id || '');
    const bDate = new Date(b.date || b._id || '');
    return aDate.getTime() - bDate.getTime();
  });
};

// Provider name mapping for better display
export const PROVIDER_NAMES = {
  pos: 'In-Store POS',
  phone_order_delivery: 'Phone Order (Delivery)',
  phone_order_pickup: 'Phone Order (Pickup)',
  ubereats: 'Uber Eats',
  doordash: 'DoorDash',
  grubhubv2: 'GrubHub',
  grubhub: 'GrubHub',
  food2: 'Website Orders',
  table_ordering: 'Table Ordering',
  online: 'Online Orders',
  mobile: 'Mobile App',
  kiosk: 'Kiosk Orders',
};

// Order type mapping for better display
export const ORDER_TYPE_NAMES = {
  takeout: 'Takeout',
  pickup: 'Pickup',
  delivery: 'Delivery',
  dine_in: 'Dine-In',
  in_store: 'In-Store',
  phone_order_delivery: 'Phone Order (Delivery)',
  phone_order_pickup: 'Phone Order (Pickup)',
  curbside: 'Curbside Pickup',
  drive_thru: 'Drive-Thru',
  catering_pickup: 'Catering (Pickup)',
  catering_delivery: 'Catering (Delivery)',
  catering: 'Catering',
  table_service: 'Table Service',
};

// Card type mapping for better display
export const CARD_TYPE_NAMES = {
  visa: 'VISA',
  mastercard: 'MASTERCARD',
  amex: 'AMEX',
  discover: 'DISCOVER',
  unknown: 'Other (Manual/External)',
};

export const DELIVERY_PROVIDER_NAMES = {
  dispatch: 'AI Dispatch',
  self: 'In-House Delivery',
};

// Provider brand that identifies self-serve kiosk orders. These come in
// through the POS provider (provider === 'pos') but should be reported as a
// separate "Kiosk Orders" channel.
export const PROVIDER_BRAND_KIOSK = 'kiosk';

// Helper function to get display name for provider
export const getProviderDisplayName = (providerId) => {
  return PROVIDER_NAMES[providerId?.toLowerCase()] || providerId;
};

// Helper that resolves the display name while accounting for the order's
// provider brand. A POS order flagged with the kiosk brand is shown as
// "Kiosk Orders" instead of "In-Store POS".
export const getProviderDisplayNameWithBrand = (providerId, providerBrand) => {
  if (
    providerId?.toLowerCase() === 'pos' &&
    providerBrand?.toLowerCase() === PROVIDER_BRAND_KIOSK
  ) {
    return PROVIDER_NAMES.kiosk;
  }
  return getProviderDisplayName(providerId);
};

export const getDeliveryProviderDisplayName = (deliveryProvider) => {
  return (
    DELIVERY_PROVIDER_NAMES[deliveryProvider?.toLowerCase()] || deliveryProvider
  );
};

// Helper function to get display name for order type
export const getOrderTypeDisplayName = (orderType) => {
  orderType = orderType || 'other';
  return ORDER_TYPE_NAMES[orderType.toLowerCase()] || orderType;
};

// Helper function to get display name for card type
export const getCardTypeDisplayName = (cardType) => {
  return CARD_TYPE_NAMES[cardType.toLowerCase()] || cardType;
};

// Comprehensive sorting for all report data
export const sortReportData = (data) => {
  const sorted = { ...data };

  // Sort money report data
  if (sorted.moneyReport) {
    if (sorted.moneyReport.tenderTypes) {
      sorted.moneyReport.tenderTypes = sortTenderTypes(
        sorted.moneyReport.tenderTypes
      );
    }
    if (sorted.moneyReport.cardTypeBreakdown) {
      sorted.moneyReport.cardTypeBreakdown = sortCardTypes(
        sorted.moneyReport.cardTypeBreakdown
      );
    }
    if (sorted.moneyReport.salesBySource) {
      sorted.moneyReport.salesBySource = sortSalesBySource(
        sorted.moneyReport.salesBySource
      );
    }
    if (sorted.moneyReport.byOrderType) {
      sorted.moneyReport.byOrderType = sortOrderTypes(
        sorted.moneyReport.byOrderType
      );
    }
    if (sorted.moneyReport.byPaymentMethod) {
      sorted.moneyReport.byPaymentMethod = sortTenderTypes(
        sorted.moneyReport.byPaymentMethod
      );
    }
  }

  // Sort coupons, discounts, rewards data
  if (sorted.couponsDiscountsRewards) {
    if (sorted.couponsDiscountsRewards.coupons) {
      sorted.couponsDiscountsRewards.coupons = sortCoupons(
        sorted.couponsDiscountsRewards.coupons
      );
    }
    if (sorted.couponsDiscountsRewards.discounts) {
      sorted.couponsDiscountsRewards.discounts = sortDiscounts(
        sorted.couponsDiscountsRewards.discounts
      );
    }
    if (sorted.couponsDiscountsRewards.refunds) {
      sorted.couponsDiscountsRewards.refunds = sortRefunds(
        sorted.couponsDiscountsRewards.refunds
      );
    }
  }

  // Sort source breakdown
  if (sorted.sourceBreakdown) {
    sorted.sourceBreakdown = sortSalesBySource(sorted.sourceBreakdown);
  }

  // Sort daily breakdown
  if (sorted.dailyBreakdown?.dailyBreakdown) {
    sorted.dailyBreakdown.dailyBreakdown = sortDailyBreakdown(
      sorted.dailyBreakdown.dailyBreakdown
    );
  }

  return sorted;
};

// Helper function to get display name for discount type
export const getDiscountTypeDisplayName = (discountType) => {
  return DISCOUNT_TYPE_NAMES[discountType?.toLowerCase()] || discountType;
};
